import React, { useEffect, useRef, useState } from 'react';
import { listSamplePresets } from '../api/catalogs';
import { ApiError } from '../api/client';
import { describeError } from '../lib/describeError';

// Modal version of the (previously stand-alone) "New valuation" preset
// chooser. Launched in-wizard from the "Use preset" link on Step 1 and
// from the dashboard empty state. `onSelect(inputs, name)` is invoked
// with the chosen preset's inputs + name; `onClose()` dismisses without
// re-seeding. "Start fresh" calls onSelect(null, 'Untitled valuation').
export default function PresetPicker({ onSelect, onClose }) {
  const [presets, setPresets] = useState(null);
  const [error, setError] = useState(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    listSamplePresets()
      .then((r) => { if (!cancelled) setPresets(r.presets || []); })
      .catch((err) => { if (!cancelled) setError(err instanceof ApiError ? err.code : 'preset_load_failed'); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    dialogRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div style={backdropStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Pick a starting preset"
        tabIndex={-1}
        style={dialogStyle}
        data-testid="preset-picker"
      >
        <div style={headerStyle}>
          <div>
            <div style={eyebrowStyle}>Pick a starting point</div>
            <h2 style={titleStyle}>Seed the wizard with a plausible profile.</h2>
          </div>
          <button type="button" onClick={onClose} style={closeBtnStyle} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div style={gridStyle}>
          <PresetCard
            name="Start fresh"
            description="Neutral defaults. Use when the prospect's dataset doesn't resemble any preset."
            tier={null}
            onClick={() => onSelect(null, 'Untitled valuation')}
            isStartFresh
          />

          {presets === null && !error && Array(4).fill(0).map((_, i) => <Skeleton key={i} />)}

          {presets && presets.map((p) => (
            <PresetCard
              key={p.name}
              name={p.name}
              description={p.description}
              tier={p.expected_tier}
              onClick={() => onSelect(p.inputs, p.name)}
            />
          ))}
        </div>

        {error && (
          <div style={errorStyle}>
            {describeError('presets', error)} You can still{' '}
            <button type="button" style={inlineBtnStyle} onClick={() => onSelect(null, 'Untitled valuation')}>
              start fresh
            </button>.
          </div>
        )}
      </div>
    </div>
  );
}

function PresetCard({ name, description, tier, isStartFresh, onClick }) {
  return (
    <button type="button" onClick={onClick} style={isStartFresh ? cardStartFreshStyle : cardStyle} data-preset={name}>
      <div style={cardHeadStyle}>
        <span style={cardNameStyle}>{name}</span>
        {tier && <span style={tierTagStyle}>{tier}</span>}
      </div>
      <p style={cardDescStyle}>{description}</p>
      <div style={cardCtaStyle}>
        {isStartFresh ? 'Open empty wizard' : 'Use this preset'} →
      </div>
    </button>
  );
}

function Skeleton() {
  return <div style={skeletonStyle} aria-hidden="true" />;
}

const backdropStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(24, 24, 27, 0.45)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '64px 24px 24px',
  zIndex: 150,
  overflowY: 'auto',
};
const dialogStyle = {
  width: '100%',
  maxWidth: '880px',
  background: 'var(--bg)',
  borderRadius: '10px',
  boxShadow: 'var(--shadow)',
  padding: '24px 28px 28px',
  outline: 'none',
};
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px', marginBottom: '20px' };
const eyebrowStyle = { fontSize: '12px', color: 'var(--accent)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' };
const titleStyle = { margin: '4px 0 0', fontSize: '22px', fontWeight: 600, color: 'var(--ink)', letterSpacing: '-0.015em', lineHeight: 1.2 };
const closeBtnStyle = {
  width: '32px', height: '32px',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  borderRadius: '50%', border: '1px solid var(--line)',
  background: 'transparent', color: 'var(--ink2)', cursor: 'pointer', flexShrink: 0,
};

const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' };

const cardBase = {
  textAlign: 'left',
  padding: '16px 18px',
  background: 'var(--surface)',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'var(--line)',
  borderRadius: '6px',
  cursor: 'pointer',
  fontFamily: 'var(--font)',
  color: 'var(--ink)',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  transition: 'border-color 0.15s',
};
const cardStyle = cardBase;
const cardStartFreshStyle = { ...cardBase, background: 'var(--paper)', borderStyle: 'dashed', borderColor: 'var(--muted2)' };
const cardHeadStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' };
const cardNameStyle = { fontSize: '14.5px', fontWeight: 600, color: 'var(--ink)' };
const tierTagStyle = { display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '999px', background: 'var(--accent-bg)', color: 'var(--accent)', fontSize: '11px', fontWeight: 500, flexShrink: 0 };
const cardDescStyle = { margin: 0, fontSize: '12.5px', color: 'var(--ink2)', lineHeight: 1.5 };
const cardCtaStyle = { fontSize: '12px', color: 'var(--accent)', fontWeight: 500, marginTop: 'auto' };

const skeletonStyle = { ...cardBase, cursor: 'default', background: 'var(--paper)', borderStyle: 'dashed', borderColor: 'var(--line-dim)', minHeight: '110px' };

const errorStyle = { marginTop: '18px', padding: '12px 14px', background: 'var(--amber-bg)', color: 'var(--amber-strong)', borderRadius: '4px', fontSize: '13px' };
const inlineBtnStyle = { color: 'var(--amber-strong)', textDecoration: 'underline', background: 'transparent', border: 'none', cursor: 'pointer', font: 'inherit' };
